import { Ban, CheckCircle2, ShieldCheck } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { ErrorState } from '../../../../components/ErrorState';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { accessLevelsApi } from '../accessLevelsApi';
import type { AccessLevel } from '../accessLevelTypes';
import { AccessLevelModal } from '../components/AccessLevelModal';

type TabKey = 'info';

/**
 * One rung on the clearance ladder.
 *
 * On the **header** layout rather than the sidebar one: a catalog row reads as a single
 * continuous thing — what it is called, where it sits, and the trail of who changed it — not a
 * record whose content splits into panels a reader chooses between.
 *
 * It earns its keep on the sentence under the rank. "Rank 2" is meaningless on its own, and the
 * comparison it stands for is the thing a reader has to get right before classifying anything.
 */
export function AccessLevelProfilePage() {
  const { id } = useParams<{ id: string }>();

  const [level, setLevel] = useState<AccessLevel | null>(null);
  const [takenRanks, setTakenRanks] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [editing, setEditing] = useState(false);
  const [tab, setTab] = useState<TabKey>('info');

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      accessLevelsApi
        .get(id, signal)
        .then((next) => {
          if (!signal?.aborted) setLevel(next);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(
            caught instanceof ApiError
              ? caught
              : new ApiError('Could not load this access level.', 0),
          );
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });

      // The whole ladder, for the edit form's rank clash. A failed fetch leaves it to the
      // server's own refusal rather than taking the page down.
      accessLevelsApi
        .options(signal)
        .then((levels) => {
          if (!signal?.aborted) setTakenRanks(levels.map((l) => l.rank));
        })
        .catch(() => undefined);
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  // A plain string is a segment that goes nowhere; the link is the way back.
  const breadcrumb: BreadcrumbSegment[] = [
    'Administration',
    { label: 'Access Levels', to: '/access-levels' },
    level?.name ?? 'Access level',
  ];

  if (error) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState
          message={error.message}
          correlationId={error.correlationId}
          onRetry={() => load()}
        />
      </div>
    );
  }

  if (loading || !level) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="access-level-profile"
        header={
          <ProfileHeader
            id="access-level-profile-header"
            icon={<ShieldCheck className="h-6 w-6" aria-hidden="true" />}
            title={level.name}
            meta={[
              `Rank ${level.rank}`,
              // Unlabelled, because "12 accounts" says what it is on its own.
              `${level.userCount} ${level.userCount === 1 ? 'account' : 'accounts'}`,
            ]}
            status={
              <StatusText tone={level.isActive ? 'active' : 'inactive'}>
                {level.isActive ? 'Active' : 'Deleted'}
              </StatusText>
            }
            createdBy={level.createdByName}
            createdAt={level.createdAt}
          />
        }
        banner={
          level.isActive ? (
            <ProfileBanner
              id="access-level-profile-banner"
              icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
              message={`This rung is offered: a record can be classified at it, and an account tagged with it. A reader tagged here reads rank ${level.rank} and weaker, and nothing stronger.`}
            />
          ) : (
            <ProfileBanner
              id="access-level-profile-banner"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message="This rung is deleted. It is no longer offered — and the records classified at it stay classified, and the accounts tagged with it keep their clearance."
            />
          )
        }
        tabs={[{ key: 'info', label: 'Info' }]}
        activeTab={tab}
        onTabChange={setTab}
      >
        <ProfileCard
          id="access-level-details"
          title="Details"
          menuItems={[
            {
              key: 'edit',
              label: 'Edit',
              permission: 'access-levels.update',
              onClick: () => setEditing(true),
            },
          ]}
        >
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Name', value: level.name },
              {
                label: 'Rank',
                value: <span className="font-mono tabular-nums">{level.rank}</span>,
              },
              {
                /*
                  The comparison spelled out, because the number alone states the opposite of
                  what most readers assume: a HIGHER rank is a WEAKER clearance. It is a fact
                  about the row and not an interpretation — the rule is `caller.rank <=
                  record.rank`, written here in the words the screen uses.
                */
                label: 'Reads',
                value:
                  level.rank === 1
                    ? 'Every record, at any level'
                    : `Records at rank ${level.rank} and weaker, plus unclassified ones. Anything stronger than rank ${level.rank} stays out of sight.`,
              },
              { label: 'Description', value: level.description },
              {
                label: 'Accounts tagged',
                value: <span className="font-mono tabular-nums">{level.userCount}</span>,
              },
            ]}
          />
        </ProfileCard>

        {/* Audit is never editable: created and updated are a record of what happened. */}
        <ProfileCard id="access-level-audit" title="Audit">
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Created by', value: level.createdByName },
              {
                label: 'Created',
                value: (
                  <time dateTime={toIso(level.createdAt)} className="font-mono">
                    {formatDateTime(level.createdAt)}
                  </time>
                ),
              },
              { label: 'Updated by', value: level.updatedByName },
              {
                label: 'Updated',
                value: level.updatedAt ? (
                  <time dateTime={toIso(level.updatedAt)} className="font-mono">
                    {formatDateTime(level.updatedAt)}
                  </time>
                ) : null,
              },
            ]}
          />
        </ProfileCard>
      </ProfileHeaderShell>

      {editing ? (
        <AccessLevelModal
          open
          editing={level}
          takenRanks={takenRanks}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      ) : null}
    </div>
  );
}
